import { apiService } from '@/services/ApiService';
import { SettingsFieldConfig } from '@/types/model/settingsModal';
import { UserManagementUserResponse } from '@/types/response/userManagementResponses';
import { GroupsResponse, SitesResponse, ExpertiseAreasResponse } from '@/types/response/lookupResponses';
import { SaveUserPrivilegesRequest } from '@/types/request/userManagementRequests';

// ---- User Privileges ----

export type PrivKey = keyof Omit<SaveUserPrivilegesRequest, 'Id'>;

export const PRIVILEGE_FIELDS: Array<{ key: PrivKey; label: string }> = [
    { key: 'EditGroups', label: 'Add/Edit Groups' },
    { key: 'EditLineItems', label: 'Add/Edit Line Items and Save' },
    { key: 'MarkForReviewLineItems', label: 'Add/Edit Line Items and Save for Review' },
    { key: 'MarkForModification', label: 'Mark Line Items for Modification' },
    { key: 'ReviewLineItems', label: 'Review Line Item Adds/Edits Saved for Review' },
    { key: 'ApproveLineItems', label: 'Approve Line Item Adds/Edits Saved for Review' },
    { key: 'EditInspectionPlans', label: 'Add/Edit/Reset Unlocked Inspection Plans' },
    { key: 'ActivateInspectionPlans', label: 'Activate Unlocked Inspection Plans' },
    { key: 'LockInspectionPlans', label: 'Lock/Unlock Inspection Plans' },
    { key: 'EditOwnInspectionResults', label: 'Add/Edit/Delete Own Inspection Results' },
    { key: 'EditInspectionResults', label: 'Add/Edit/Delete Others Inspection Results' },
    { key: 'ApproveInspectionResults', label: 'Approve Inspection Results' },
    { key: 'EditOwnCAs', label: 'Add/Edit Own CAs/Respond to CAs' },
    { key: 'EditCAs', label: 'Edit Others CAs' },
    { key: 'ValidateCAs', label: 'Validate CAs' },
    { key: 'AcceptCAs', label: 'Accept CAs' },
    { key: 'DeleteReopenCAs', label: 'Delete/Reopen CAs' },
    { key: 'EditAttachments', label: 'Overwrite/Delete Attachment Files' },
];

export type PrivilegesForm = Record<PrivKey, boolean>;

export const privilegesEmptyForm: PrivilegesForm = PRIVILEGE_FIELDS.reduce(
    (acc, { key }) => ({ ...acc, [key]: false }), {} as PrivilegesForm,
);

export const privilegesInitialData = (u: UserManagementUserResponse | null): PrivilegesForm | null =>
    u ? PRIVILEGE_FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: u[key] }), {} as PrivilegesForm) : null;

export const privilegesFields: SettingsFieldConfig<PrivilegesForm>[] = PRIVILEGE_FIELDS.map(({ key, label }) => ({
    key, label, type: 'checkbox',
    formatView: value => (value ? 'Enabled' : 'Disabled'),
}));

export const savePrivileges = (userId: number) => async (form: PrivilegesForm): Promise<void> => {
    await apiService.saveUserPrivileges({ Id: userId, ...form });
};

// ---- User Preferences (User Management) ----

export interface UserManagementPreferencesForm {
    startupPage: string;
    groupIds: string[];
    siteIds: string[];
    areaIds: string[];
    weight: string;
    interval: string;
    summaryDashboardDays: string;
    statusDashboardDays: string;
    inputDelayHours: string;
    repeatCorrectiveActionCount: string;
    checklistScore: string;
    toDoDashboardDays: string;
    inspectionResultsDays: string;
    correctiveActionAlert: boolean;
    outstandingInspectionAlert: boolean;
    lowChecklistScoreAlert: boolean;
    lowChecklistScore: string;
    readyForApprovalAlert: boolean;
    flaggedAsExceptionalAlert: boolean;
}

const STARTUP_PAGES = ['home', 'inspecting', 'planning', 'corrective-actions', 'reporting'];
const WEIGHT_OPTIONS = ['standard', 'alternate'];
const INTERVAL_OPTIONS = ['weekly', 'monthly', 'quarterly', 'annually'];

export const userManagementPreferencesEmptyForm: UserManagementPreferencesForm = {
    startupPage: '', groupIds: [], siteIds: [], areaIds: [], weight: '', interval: '',
    summaryDashboardDays: '', statusDashboardDays: '', inputDelayHours: '', repeatCorrectiveActionCount: '',
    checklistScore: '', toDoDashboardDays: '', inspectionResultsDays: '',
    correctiveActionAlert: false, outstandingInspectionAlert: false, lowChecklistScoreAlert: false,
    lowChecklistScore: '', readyForApprovalAlert: false, flaggedAsExceptionalAlert: false,
};

export const userManagementPreferencesInitialData = (u: UserManagementUserResponse | null): UserManagementPreferencesForm | null =>
    u ? {
        startupPage: u.StartupPage ?? '',
        groupIds: (u.GroupIds ?? []).map(String),
        siteIds: (u.SiteIds ?? []).map(String),
        areaIds: (u.AreaIds ?? []).map(String),
        weight: u.Weight ?? '',
        interval: u.Interval ?? '',
        summaryDashboardDays: u.SummaryDashboardDays?.toString() ?? '',
        statusDashboardDays: u.StatusDashboardDays?.toString() ?? '',
        inputDelayHours: u.InputDelayHours?.toString() ?? '',
        repeatCorrectiveActionCount: u.RepeatCorrectiveActionCount?.toString() ?? '',
        checklistScore: u.ChecklistScore?.toString() ?? '',
        toDoDashboardDays: u.ToDoDashboardDays?.toString() ?? '',
        inspectionResultsDays: u.InspectionResultsDays?.toString() ?? '',
        correctiveActionAlert: u.CorrectiveActionAlert ?? false,
        outstandingInspectionAlert: u.OutstandingInspectionAlert ?? false,
        lowChecklistScoreAlert: u.LowChecklistScoreAlert ?? false,
        lowChecklistScore: u.LowChecklistScore?.toString() ?? '',
        readyForApprovalAlert: u.ReadyForApprovalAlert ?? false,
        flaggedAsExceptionalAlert: u.FlaggedAsExceptionalAlert ?? false,
    } : null;

const requiredInt = (label: string) => (v: unknown): string | undefined => {
    const n = parseInt(String(v), 10);
    if (!String(v).trim()) return `${label} is required.`;
    if (isNaN(n) || n < 0) return `${label} must be a non-negative integer.`;
    return undefined;
};

const requiredDecimal = (label: string) => (v: unknown): string | undefined => {
    const n = parseFloat(String(v));
    if (!String(v).trim()) return `${label} is required.`;
    if (isNaN(n) || n < 0) return `${label} must be a non-negative number.`;
    return undefined;
};

export const userManagementPreferencesFields = (
    groups: GroupsResponse[], sites: SitesResponse[], areas: ExpertiseAreasResponse[],
): SettingsFieldConfig<UserManagementPreferencesForm>[] => [
    { key: 'startupPage', label: 'Start-up Page', type: 'select', options: STARTUP_PAGES.map(p => ({ value: p, label: p })) },
    { key: 'weight', label: 'Weight', type: 'select', options: WEIGHT_OPTIONS.map(o => ({ value: o, label: o })) },
    { key: 'interval', label: 'Interval', type: 'select', options: INTERVAL_OPTIONS.map(o => ({ value: o, label: o })) },
    { key: 'groupIds', label: 'Groups', type: 'multiselect', options: groups.map(g => ({ value: g.Id.toString(), label: g.Name })) },
    { key: 'siteIds', label: 'Sites', type: 'multiselect', options: sites.map(s => ({ value: s.Id.toString(), label: s.Name })) },
    { key: 'areaIds', label: 'Areas of Expertise', type: 'multiselect', options: areas.map(a => ({ value: a.Id.toString(), label: a.Name })) },
    { key: 'summaryDashboardDays', label: 'Summary Dashboard Days', type: 'number', validate: requiredInt('Summary Dashboard Days') },
    { key: 'statusDashboardDays', label: 'Status Dashboard Days', type: 'number', validate: requiredInt('Status Dashboard Days') },
    { key: 'inputDelayHours', label: 'Input Delay Hours', type: 'number', validate: requiredInt('Input Delay Hours') },
    { key: 'repeatCorrectiveActionCount', label: 'Repeat CA Count', type: 'number', validate: requiredInt('Repeat CA Count') },
    { key: 'checklistScore', label: 'Checklist Score (\u2264 alert)', type: 'number', validate: requiredDecimal('Checklist Score') },
    { key: 'toDoDashboardDays', label: 'To Do Dashboard Days', type: 'number', validate: requiredInt('To Do Dashboard Days') },
    { key: 'inspectionResultsDays', label: 'Inspection Results Days', type: 'number', validate: requiredInt('Inspection Results Days') },
    { key: 'lowChecklistScore', label: 'Low Checklist Score', type: 'number', validate: requiredDecimal('Low Checklist Score') },
    { key: 'correctiveActionAlert', label: 'CA Status Change Email Alert', type: 'checkbox' },
    { key: 'outstandingInspectionAlert', label: 'Outstanding Inspection Email Alert', type: 'checkbox' },
    { key: 'lowChecklistScoreAlert', label: 'Low Checklist Score Email Alert', type: 'checkbox' },
    { key: 'readyForApprovalAlert', label: 'Inspection Ready for Approval Email Alert', type: 'checkbox' },
    { key: 'flaggedAsExceptionalAlert', label: 'Inspection Flagged as Exceptional Email Alert', type: 'checkbox' },
];

export const saveUserManagementPreferences = (userId: number) => async (form: UserManagementPreferencesForm): Promise<void> => {
    await apiService.saveUserManagementPreferences({
        Id: userId,
        startupPage: form.startupPage,
        groupIds: form.groupIds.map(Number),
        siteIds: form.siteIds.map(Number),
        areaIds: form.areaIds.map(Number),
        weight: form.weight,
        interval: form.interval,
        summaryDashboardDays: form.summaryDashboardDays,
        statusDashboardDays: form.statusDashboardDays,
        inputDelayHours: form.inputDelayHours,
        repeatCorrectiveActionCount: form.repeatCorrectiveActionCount,
        checklistScore: parseFloat(form.checklistScore),
        toDoDashboardDays: form.toDoDashboardDays,
        inspectionResultsDays: form.inspectionResultsDays,
        correctiveActionAlert: form.correctiveActionAlert,
        outstandingInspectionAlert: form.outstandingInspectionAlert,
        lowChecklistScoreAlert: form.lowChecklistScoreAlert,
        lowChecklistScore: parseFloat(form.lowChecklistScore),
        readyForApprovalAlert: form.readyForApprovalAlert,
        flaggedAsExceptionalAlert: form.flaggedAsExceptionalAlert,
    });
};
